import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { supabase } from '../../lib/supabase';
import { usePopup } from '../../contexts/PopupContext';
import SingleEvent from '../../classes/events/SingleEvent';
import SubTabs from '../layout/SubTabs';
import EventInfoSection from './EventInfoSection';
import EventUsersList from './EventUsersList';
import { EventChatSection } from './chat';
import EventQuickActions from './EventQuickActions';
import EventLikeButton from './EventLikeButton';
import Loading from '../layout/Loading';
import { PopularCategoryIcon } from '../discover/PopularCategoryIcons';
import { EventCardIcon } from '../discover/EventCardIcons';
import { getFriendStatus } from '../../utils/friendsService';
import { getProfileCardDisplayName } from '../../utils/profileDisplayName';
import { getEventCategoryVisual } from '../../config/eventCategoryVisuals';
import './EventDetails.css';

// ============ Event Details Cache ============
const DETAILS_CACHE_KEY = 'eventDetailsCache';
const DETAILS_CACHE_TTL = 1000 * 60 * 10; // 10 minut TTL

const getDetailsCache = () => {
  try {
    const cached = localStorage.getItem(DETAILS_CACHE_KEY);
    if (!cached) return {};
    return JSON.parse(cached);
  } catch {
    return {};
  }
};

const saveDetailsCache = (cache) => {
  try {
    const entries = Object.entries(cache);
    if (entries.length > 30) {
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      entries.slice(0, entries.length - 30).forEach(([k]) => delete cache[k]);
    }
    localStorage.setItem(DETAILS_CACHE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.warn('Failed to save event details cache:', err);
  }
};

const getCachedDetails = (eventId) => {
  if (!eventId) return null;
  const cache = getDetailsCache();
  const entry = cache[eventId];
  if (!entry) return null;
  if (Date.now() - entry.timestamp > DETAILS_CACHE_TTL) {
    delete cache[eventId];
    saveDetailsCache(cache);
    return null;
  }
  return entry.data;
};

const setCachedDetails = (eventId, data) => {
  if (!eventId) return;
  const cache = getDetailsCache();
  cache[eventId] = { data, timestamp: Date.now() };
  saveDetailsCache(cache);
};

function EventDetails({ eventId, variant = 'popup', onClose }) {
  const { t } = useTranslation(['events', 'shared']);
  const { openPopup } = usePopup();
  
  // Załaduj dane z cache przy inicjalizacji
  const cachedData = eventId ? getCachedDetails(eventId) : null;
  
  const [activeSection, setActiveSection] = useState('info'); // 'info', 'users', 'chat'
  const [event, setEvent] = useState(cachedData?.event || null);
  const [host, setHost] = useState(cachedData?.host || null);
  const [currentUser, setCurrentUser] = useState(null);
  const [participantsCount, setParticipantsCount] = useState(cachedData?.participantsCount || 0);
  const [userApplication, setUserApplication] = useState(cachedData?.userApplication || null);
  const [isParticipant, setIsParticipant] = useState(cachedData?.isParticipant || false);
  const [pendingApplicationsCount, setPendingApplicationsCount] = useState(0);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [hasInvitation, setHasInvitation] = useState(cachedData?.hasInvitation || false);
  // Nie pokazuj loading jeśli mamy dane z cache
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState(null);
  const [isHeaderExpanded, setIsHeaderExpanded] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const channelRef = useRef(null);
  const eventRef = useRef(cachedData?.event || null);
  const activeSectionRef = useRef('info');
  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const [hostFriendAccepted, setHostFriendAccepted] = useState(false);

  useEffect(() => {
    if (!host?.id) {
      setHostFriendAccepted(false);
      return;
    }
    if (!currentUser?.id) {
      setHostFriendAccepted(false);
      return;
    }
    if (currentUser.id === host.id) {
      setHostFriendAccepted(true);
      return;
    }
    let cancelled = false;
    getFriendStatus(currentUser.id, host.id).then((status) => {
      if (!cancelled) setHostFriendAccepted(status === 'accepted');
    });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id, host?.id]);

  useEffect(() => {
    if (eventId) {
      loadEventData();
      setupRealtimeSubscription();

      return () => {
        // Cleanup subscription
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Reset unread messages count when entering chat section
  useEffect(() => {
    if (activeSection === 'chat' && currentUser?.id && eventId && unreadMessagesCount > 0) {
      const markAsRead = async () => {
        await SingleEvent.markMessagesAsRead(eventId, currentUser.id);
        setUnreadMessagesCount(0);
      };
      markAsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, currentUser?.id, eventId]);

  const handleScrollDown = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setIsHeaderExpanded(false);
    // Scroll to top of content
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  const handleRestoreHeader = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setIsHeaderExpanded(true);
    // Scroll to top
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  const handleContentClick = (e) => {
    // Only hide header if it's expanded and user clicks in content area
    // Don't trigger on clicks on interactive elements (buttons, links, etc.)
    const target = e.target;
    const isInteractive = target.tagName === 'BUTTON' || 
                         target.tagName === 'A' || 
                         target.closest('button') || 
                         target.closest('a') ||
                         target.closest('input') ||
                         target.closest('textarea') ||
                         target.closest('select');
    
    if (isHeaderExpanded && !isTransitioning && !isInteractive) {
      // Check if click is in content area (not in header)
      const contentElement = contentRef.current;
      if (contentElement && contentElement.contains(target)) {
        handleScrollDown();
      }
    }
  };

  // Scroll detection for smooth layer transitions
  useEffect(() => {
    // Wait for content to be loaded and rendered
    if (loading || !event || variant !== 'popup') return;

    let cleanup = null;
    let timeoutId = null;

    const setupScrollListeners = () => {
      const contentElement = contentRef.current;
      if (!contentElement) {
        // Retry after a short delay if element is not yet available
        timeoutId = setTimeout(setupScrollListeners, 50);
        return;
      }

      let touchStartY = 0;
      let isTouching = false;

      const handleWheel = (e) => {
        if (isTransitioning) {
          e.preventDefault();
          return;
        }

        const currentScrollTop = contentElement.scrollTop;
        const deltaY = e.deltaY;
        const threshold = 10; // Minimum scroll delta to trigger
        
        // When header is expanded and scrolling down at the top
        if (isHeaderExpanded && deltaY > threshold && currentScrollTop <= 5) {
          e.preventDefault();
          handleScrollDown();
          return;
        }
        
        // When header is collapsed and scrolling up at the top
        if (!isHeaderExpanded && deltaY < -threshold && currentScrollTop <= 5) {
          e.preventDefault();
          handleRestoreHeader();
          return;
        }
      };

      const handleTouchStart = (e) => {
        touchStartY = e.touches[0].clientY;
        isTouching = true;
      };

      const handleTouchMove = (e) => {
        if (isTransitioning || !isTouching) {
          if (isTransitioning) {
            e.preventDefault();
          }
          return;
        }

        const currentY = e.touches[0].clientY;
        const deltaY = touchStartY - currentY;
        const currentScrollTop = contentElement.scrollTop;
        const threshold = 30; // Minimum swipe distance

        // When header is expanded and swiping down at the top
        if (isHeaderExpanded && deltaY < -threshold && currentScrollTop <= 5) {
          e.preventDefault();
          handleScrollDown();
          isTouching = false;
          return;
        }

        // When header is collapsed and swiping up at the top
        if (!isHeaderExpanded && deltaY > threshold && currentScrollTop <= 5) {
          e.preventDefault();
          handleRestoreHeader();
          isTouching = false;
          return;
        }
      };

      const handleTouchEnd = () => {
        isTouching = false;
      };

      // Use wheel event for smooth scroll detection
      contentElement.addEventListener('wheel', handleWheel, { passive: false });
      contentElement.addEventListener('touchstart', handleTouchStart, { passive: true });
      contentElement.addEventListener('touchmove', handleTouchMove, { passive: false });
      contentElement.addEventListener('touchend', handleTouchEnd, { passive: true });

      // Store cleanup function
      cleanup = () => {
        contentElement.removeEventListener('wheel', handleWheel);
        contentElement.removeEventListener('touchstart', handleTouchStart);
        contentElement.removeEventListener('touchmove', handleTouchMove);
        contentElement.removeEventListener('touchend', handleTouchEnd);
      };
    };

    // Setup listeners with a small delay to ensure DOM is ready
    timeoutId = setTimeout(setupScrollListeners, 0);
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (cleanup) {
        cleanup();
      }
    };
  }, [variant, isHeaderExpanded, isTransitioning, loading, event, handleScrollDown, handleRestoreHeader]);

  const loadEventData = async () => {
    try {
      // Pokaż loading tylko jeśli nie mamy danych z cache
      if (!event) {
        setLoading(true);
      }
      setError(null);

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);

      // Load all event data using SingleEvent class
      const eventData = await SingleEvent.loadAll(eventId, user?.id);

      if (eventData.errors.event) {
        throw eventData.errors.event;
      }

      setEvent(eventData.event);
      eventRef.current = eventData.event;
      setHost(eventData.host);
      setParticipantsCount(eventData.participantsCount);
      setUserApplication(eventData.userApplication);

      // Check if user is actually a participant
      let isUserParticipant = false;
      let userHasInvitation = false;
      
      if (user?.id && eventData.event) {
        isUserParticipant = await SingleEvent.isParticipantInDB(eventId, user.id);
        setIsParticipant(isUserParticipant);

        // Check if user has a pending invitation to this event
        if (!isUserParticipant) {
          const { data: invitationData } = await supabase
            .from('event_invitations')
            .select('id')
            .eq('event_id', eventId)
            .eq('invitee_id', user.id)
            .eq('status', 'pending')
            .single();
          
          userHasInvitation = !!invitationData;
          setHasInvitation(userHasInvitation);
        } else {
          setHasInvitation(false);
        }
      } else {
        setIsParticipant(false);
        setHasInvitation(false);
      }

      // Load pending applications count for host
      if (user?.id && eventData.event?.host_id === user.id) {
        const applicationsResult = await SingleEvent.loadApplications(eventId, 'pending');
        if (!applicationsResult.error) {
          setPendingApplicationsCount(applicationsResult.data?.length || 0);
        }
      } else {
        setPendingApplicationsCount(0);
      }

      // Load unread messages count
      if (user?.id) {
        const unreadResult = await SingleEvent.loadUnreadMessagesCount(eventId, user.id);
        if (!unreadResult.error) {
          setUnreadMessagesCount(unreadResult.count || 0);
        }
      } else {
        setUnreadMessagesCount(0);
      }

      // Zapisz do cache
      setCachedDetails(eventId, {
        event: eventData.event,
        host: eventData.host,
        participantsCount: eventData.participantsCount,
        userApplication: eventData.userApplication,
        isParticipant: isUserParticipant,
        hasInvitation: userHasInvitation
      });
    } catch (err) {
      console.error('Error loading event:', err);
      setError(err.message || t('events.details.error'));
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    // Clean up existing subscription if any
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Subscribe to changes in event_participants and event_applications
    const channel = supabase
      .channel(`event-participants-footer-${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'event_participants',
          filter: `event_id=eq.${eventId}`
        },
        async (payload) => {
          // Get current user for the callback
          const { data: { user } } = await supabase.auth.getUser();
          
          // Reload participants count and user status when there's a change
          const [countResult, userApplicationResult] = await Promise.all([
            SingleEvent.loadParticipantsCount(eventId),
            user?.id ? SingleEvent.loadUserApplication(eventId, user.id) : Promise.resolve({ data: null, error: null })
          ]);
          
          if (!countResult.error && countResult.count !== null) {
            setParticipantsCount(countResult.count);
          }
          
          if (userApplicationResult.data !== undefined) {
            setUserApplication(userApplicationResult.data);
          }

          // Check if user is participant
          if (user?.id) {
            const isUserParticipant = await SingleEvent.isParticipantInDB(eventId, user.id);
            setIsParticipant(isUserParticipant);
            
            // Check if user has a pending invitation
            if (!isUserParticipant) {
              const { data: invitationData } = await supabase
                .from('event_invitations')
                .select('id')
                .eq('event_id', eventId)
                .eq('invitee_id', user.id)
                .eq('status', 'pending')
                .single();
              
              setHasInvitation(!!invitationData);
            } else {
              setHasInvitation(false);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'event_invitations',
          filter: `event_id=eq.${eventId}`
        },
        async (payload) => {
          // Get current user for the callback
          const { data: { user } } = await supabase.auth.getUser();
          
          // Check if user has a pending invitation
          if (user?.id) {
            const { data: invitationData } = await supabase
              .from('event_invitations')
              .select('id')
              .eq('event_id', eventId)
              .eq('invitee_id', user.id)
              .eq('status', 'pending')
              .single();
            
            setHasInvitation(!!invitationData);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'event_applications',
          filter: `event_id=eq.${eventId}`
        },
        async (payload) => {
          // Get current user for the callback
          const { data: { user } } = await supabase.auth.getUser();
          
          // Reload user application status
          if (user?.id) {
            const userApplicationResult = await SingleEvent.loadUserApplication(eventId, user.id);
            if (userApplicationResult.data !== undefined) {
              setUserApplication(userApplicationResult.data);
            }
          }

          // Update pending applications count for host
          if (user?.id && eventRef.current?.host_id === user.id) {
            const applicationsResult = await SingleEvent.loadApplications(eventId, 'pending');
            if (!applicationsResult.error) {
              setPendingApplicationsCount(applicationsResult.data?.length || 0);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*', // INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'event_messages',
          filter: `event_id=eq.${eventId}`
        },
        async (payload) => {
          // Get current user for the callback
          const { data: { user } } = await supabase.auth.getUser();
          
          // Update unread messages count (tylko jeśli nie jesteśmy na zakładce czatu)
          if (user?.id && activeSectionRef.current !== 'chat') {
            const unreadResult = await SingleEvent.loadUnreadMessagesCount(eventId, user.id);
            if (!unreadResult.error) {
              setUnreadMessagesCount(unreadResult.count || 0);
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'events',
          filter: `id=eq.${eventId}`
        },
        async (payload) => {
          // Reload event data when is_cancelled changes
          if (payload.new && 'is_cancelled' in payload.new) {
            await loadEventData();
          }
        }
      )
      .subscribe();

    channelRef.current = channel;
  };

  const handleHostClick = () => {
    if (host) {
      openPopup('profile', { userId: host.id }, 0);
    }
  };

  const handleStatusChange = async () => {
    // Szybko zaktualizuj tylko status użytkownika bez przeładowywania całych danych
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.id) {
      const [userApplicationResult, isUserParticipant] = await Promise.all([
        SingleEvent.loadUserApplication(eventId, user.id),
        SingleEvent.isParticipantInDB(eventId, user.id)
      ]);
      
      if (userApplicationResult.data !== undefined) {
        setUserApplication(userApplicationResult.data);
      }
      setIsParticipant(isUserParticipant);
    }
  };

  if (loading) {
    return (
      <div className={`event-details event-details-${variant}`}>
        <Loading className="event-details-loading" size="medium" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className={`event-details event-details-${variant}`}>
        <div className="event-details-error">
          <p>{error || t('events.details.error')}</p>
        </div>
      </div>
    );
  }

  const isHost = currentUser && event.host_id === currentUser.id;
  const eventDetailsStatusClass = new SingleEvent(event.id, event)
    .getEventStatus()
    .replace(/_/g, '-');
  const categoryVisual = getEventCategoryVisual(event.event_type);
  const categoryHeaderStyle = {
    '--event-category-color': categoryVisual.color,
    '--event-category-soft': categoryVisual.softColor,
  };

  const hostDisplayName = getProfileCardDisplayName(host, {
    allowRealName: true,
    nameNotSetLabel: t('events.details.unknownHost'),
  });

  // Aktualizuj ref przy zmianie activeSection
  activeSectionRef.current = activeSection;

  // Build sections based on user role
  const sections = [];
  
  // Info section - always visible
  sections.push({ id: 'info', label: t('events.page.sections.info') });
  
  // Users section
  if (isHost) {
    sections.push({ id: 'users', label: t('events.page.sections.users'), hasNotification: pendingApplicationsCount > 0 });
  } else {
    sections.push({ id: 'users', label: t('events.page.sections.users') });
  }
  
  // Chat section
  sections.push({ id: 'chat', label: t('events.page.sections.chat'), hasNotification: unreadMessagesCount > 0 });

  return (
    <div className={`event-details event-details-${variant} event-details-status-${eventDetailsStatusClass} ${!isHeaderExpanded ? 'event-details-header-collapsed' : ''}`}>
      {/* Collapsed banner - shown when header is collapsed */}
      {variant === 'popup' && !isHeaderExpanded && (
        <div 
          className={`event-details-header-collapsed-banner ${!event.banner_url ? `event-details-header-category-bg event-details-header-category-${categoryVisual.key}` : ''}`}
          onClick={handleRestoreHeader}
          style={event.banner_url ? {
            backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.6)), url(${event.banner_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          } : categoryHeaderStyle}
        >
          {!event.banner_url && (
            <PopularCategoryIcon variant={categoryVisual.key} className="event-details-category-watermark event-details-category-watermark-collapsed" />
          )}
          <div className="event-details-header-collapsed-content">
            <h2 className="event-details-header-collapsed-title">{event.title}</h2>
            <span className="event-details-header-collapsed-hint">{t('events.page.restoreHeader')}</span>
          </div>
          {onClose && (
            <button 
              className="event-details-close-button event-details-close-button-collapsed" 
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label={t('events.page.close')}
            >
              ×
            </button>
          )}
        </div>
      )}

      {/* Full Header with gradient or banner - only for popup variant */}
      {variant === 'popup' && (
        <div 
          className={`event-details-header-wrapper ${!event.banner_url ? 'event-details-header-wrapper-no-banner event-details-header-wrapper-category' : ''} ${!isHeaderExpanded ? 'event-details-header-wrapper-hidden' : ''}`}
        >
          <div 
            ref={headerRef}
            className={`event-details-header ${event.banner_url ? 'event-details-header-with-banner' : `event-details-header-category-bg event-details-header-category-${categoryVisual.key}`} ${!isHeaderExpanded ? 'event-details-header-hidden' : ''} ${isTransitioning ? 'event-details-header-transitioning' : ''}`}
            style={event.banner_url ? {
              backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.6)), url(${event.banner_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            } : categoryHeaderStyle}
          >
          {!event.banner_url && (
            <PopularCategoryIcon variant={categoryVisual.key} className="event-details-category-watermark" />
          )}
          <div className="event-details-header-content">
            {/* Top header row: Title + Close button */}
            <div className="event-details-header-top">
              <h1 className="event-details-title">{event.title}</h1>
              {onClose && (
                <button 
                  className="event-details-close-button event-details-close-button-expanded" 
                  onClick={onClose}
                  aria-label={t('events.page.close')}
                >
                  ×
                </button>
              )}
            </div>
            
            {/* Second row: Organizer + Action buttons + Scroll down button */}
            <div className="event-details-header-bottom">
              <div className="event-details-organizer">
                <span className="event-details-organizer-label">{t('events.details.host')}:</span>
                <button
                  className="event-details-organizer-link"
                  onClick={handleHostClick}
                  type="button"
                  disabled={!host}
                >
                  {hostDisplayName}
                </button>
              </div>
                              {/* Collapse header handle */}
                {isHeaderExpanded && (
                  <button 
                    className="event-details-collapse-handle"
                    onClick={handleScrollDown}
                    type="button"
                    aria-label={t('events.page.collapseHeader')}
                    title={t('events.page.collapseHeader')}
                  >
                    <span className="event-details-collapse-icon">‹</span>
                    <span className="event-details-collapse-icon">‹</span>
                  </button>
                )}
              <div className="event-details-header-bottom-right">
                    <div className="event-details-header-actions">
                            <button
                                className="event-details-action-button"
                                type="button"
                                title={event.is_cancelled || event.status === 'ended' 
                                    ? (event.is_cancelled ? t('events.share.eventCancelled') : t('events.share.eventEnded'))
                                    : t('events.page.share')}
                                onClick={() => {
                                    if (event.is_cancelled || event.status === 'ended') {
                                        toast.error(event.is_cancelled 
                                            ? t('events.share.eventCancelled') 
                                            : t('events.share.eventEnded'));
                                        return;
                                    }
                                    openPopup('share-event', { eventId: event.id, eventTitle: event.title });
                                }}
                                disabled={event.is_cancelled || event.status === 'ended'}
                            >
                                🔗
                            </button>
                            <EventLikeButton eventId={event.id} />
                        </div>
                

              </div>
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="event-details-tabs-container">
        <SubTabs tabs={sections} activeTab={activeSection} setActiveTab={setActiveSection} />
      </div>

      {/* Content */}
      <div 
        className={`event-details-content ${activeSection === 'chat' ? 'event-details-content-chat' : ''} ${isTransitioning ? 'event-details-content-transitioning' : ''} ${isHeaderExpanded ? 'event-details-content-clickable' : ''}`}
        ref={contentRef}
        onClick={handleContentClick}
      >
        {/* Invitation Notification - shown at the top of content */}
        {hasInvitation && !isHost && variant === 'popup' && (
          <div className="event-details-invitation-notification">
            <span className="event-details-invitation-notification-icon">✉️</span>
            <span className="event-details-invitation-notification-text">
              {t('events.invitations.hasInvitation', 'Otrzymałeś zaproszenie do tego wydarzenia')}
            </span>
          </div>
        )}

        {activeSection === 'info' && (
          <EventInfoSection
            event={event}
            host={host}
            participantsCount={participantsCount}
            isHost={isHost}
            isParticipant={isParticipant}
            currentUser={currentUser}
            hasInvitation={hasInvitation}
            onEventCancelled={() => {
              if (onClose) {
                onClose();
              } else {
                window.location.reload();
              }
            }}
            onEventUpdated={(updatedEvent) => {
              setEvent(updatedEvent);
              eventRef.current = updatedEvent;
            }}
          />
        )}

        {activeSection === 'users' && (
          <EventUsersList
            eventId={event.id}
            event={event}
            currentUser={currentUser}
            isHost={isHost}
            onStatusChange={handleStatusChange}
          />
        )}

        {activeSection === 'chat' && (
          <EventChatSection
            eventId={event.id}
            isHost={isHost}
          />
        )}
      </div>

      {/* Footer - only for popup variant */}
      {variant === 'popup' && (
        <div className="event-details-footer">
          {currentUser && !isHost ? (
            <EventQuickActions
              eventId={event.id}
              event={event}
              userApplication={userApplication}
              isParticipant={isParticipant}
              isHost={isHost}
              participantsCount={participantsCount}
              maxParticipants={event.max_participants}
              onStatusChange={handleStatusChange}
            />
          ) : (
            <>
              <div className="event-details-footer-left">
                <span className="event-details-footer-participants">
                  <EventCardIcon type="users" className="event-details-footer-icon" size={17} />
                  <strong>{participantsCount}/{event.max_participants}</strong>
                  <span className="event-details-footer-spots">
                    ({event.max_participants - participantsCount} {t('events.page.footer.spotsLeft')})
                  </span>
                </span>
              </div>
              {isHost && (
                <div className="event-details-footer-right">
                  <span className="event-details-host-badge">
                    <EventCardIcon type="host" className="event-details-host-badge-icon" size={15} />
                    {t('events.page.footer.youAreHost')}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default EventDetails;
